import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Input, Spin, Button, message } from "antd";
import { Paperclip } from "@phosphor-icons/react";
import {
  getWaitingOnWorkItems,
  attachDocumentToWorkItem,
} from "../../service/inbox";
import { toInstructionHtml } from "./CreateWorkItemModal";
import { isLiveReviewCopy } from "../../utils/reviewCopy";
import {
  InboxModalErrorBanner,
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
} from "../AIPane/AIPaneStyled";

/**
 * Attach to Work Item — the conveyor belt's return move.
 *
 * Matthew sends Anne "Get statement — migraines"; the statement arrives
 * by email or mail into this inbox. Whoever is reading it picks the open
 * work item it answers, adds a word if they want, and confirms. The
 * document link lands in the item's thread, and a Sent item volleys
 * straight back to the requester's court with one bell — no separate
 * "now go tell Matthew" step.
 *
 * The picker is the matter's open (Sent) work items from the waiting-on
 * endpoint — the same list the banner shows. `initialWorkItemId` lets
 * the banner's per-item Attach button arrive preselected; the footer's
 * generic button starts blank.
 */
const AttachToWorkItemModal = ({
  open,
  documentId,
  initialWorkItemId = null,
  onClose,
  onAttached = undefined,
}) => {
  const [loadStatus, setLoadStatus] = useState("idle");
  const [items, setItems] = useState([]);
  const [workItemId, setWorkItemId] = useState(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let stale = false;
    setWorkItemId(initialWorkItemId || null);
    setNote("");
    setError(null);
    setLoadStatus("loading");
    getWaitingOnWorkItems(documentId)
      .then((response) => {
        if (stale) return;
        const data = response?.data || {};
        // The copy of a live document review is not something paper
        // can answer: it moves only from the review (Approve / Send
        // back), and the server refuses to volley it anyway.
        setItems((data.items || []).filter((item) => !isLiveReviewCopy(item)));
        setLoadStatus("ready");
      })
      .catch(() => {
        if (stale) return;
        setLoadStatus("failed");
        setError(
          "Couldn't reach the new Salesforce to load this matter's open work items."
        );
      });
    return () => {
      // The reader can switch documents with the modal mounted; the
      // picker must never show the previous matter's items (Bugbot, #634).
      stale = true;
    };
  }, [open, documentId, initialWorkItemId]);

  const options = useMemo(
    () =>
      items.map((item) => ({
        value: item.workItemId,
        label: `${item.itemNumber} — ${item.subject}${
          item.holderName ? ` (with ${item.holderName})` : ""
        }`,
      })),
    [items]
  );

  const selected = useMemo(
    () => items.find((item) => item.workItemId === workItemId) || null,
    [items, workItemId]
  );

  const canSubmit =
    loadStatus === "ready" && workItemId != null && !submitting;

  // Work Items v2 (Matthew, 11/2): two ways out. **Attach** adds the
  // document and leaves the item where it is — with three records
  // expected, the first must not bounce it back. **Attach & return** is
  // the one-click for a single paper: it still volleys the item home (or,
  // on a route, passes it to the next stop).
  const submit = useCallback(
    (andReturn) => {
      if (!canSubmit) return;
      setSubmitting(true);
      setError(null);
      const trimmedNote = note.trim();
      attachDocumentToWorkItem(documentId, {
        work_item_id: workItemId,
        andReturn,
        ...(trimmedNote ? { note: toInstructionHtml(trimmedNote) } : {}),
      })
        .then((response) => {
          const volleyed = Boolean(response?.data?.volleyedToRequester);
          const passedTo = response?.data?.passedTo;
          const number = selected?.itemNumber;
          const requester = selected?.requestedByName;
          message.success(
            volleyed
              ? `Attached — ${number || "the work item"} is back in ${
                  requester ? `${requester}'s` : "the requester's"
                } court.`
              : passedTo
              ? `Attached — ${number || "the work item"} passed on to ${passedTo}.`
              : `Attached to ${number || "the work item"}${
                  andReturn ? "" : " — it stays where it is"
                }.`
          );
          if (onAttached) onAttached();
          onClose();
        })
        .catch((err) => {
          setError(
            err?.response?.data?.detail ||
              "Couldn't attach the document. Please try again."
          );
        })
        .finally(() => setSubmitting(false));
    },
    [canSubmit, documentId, workItemId, note, selected, onAttached, onClose]
  );
  const handleSubmit = useCallback(() => submit(true), [submit]);
  const handleAttachOnly = useCallback(() => submit(false), [submit]);

  const title = (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <Paperclip size={16} weight="bold" />
      Attach to Work Item
    </span>
  );

  const nothingWaiting = loadStatus === "ready" && items.length === 0;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Attach & return"
      okButtonProps={{
        disabled: !canSubmit,
        loading: submitting,
        style: nothingWaiting ? { display: "none" } : undefined,
      }}
      cancelText={nothingWaiting ? "Close" : "Cancel"}
      title={title}
      destroyOnClose
      footer={(_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          {!nothingWaiting && (
            <Button
              onClick={handleAttachOnly}
              disabled={!canSubmit}
              loading={submitting}
              data-testid="attach-only"
              title="Add the document; the item stays with whoever holds it"
            >
              Attach
            </Button>
          )}
          <OkBtn />
        </>
      )}
    >
      {loadStatus === "loading" && (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <Spin />
          <InboxModalHint style={{ marginTop: 8 }}>
            Loading this matter's open work items…
          </InboxModalHint>
        </div>
      )}

      {nothingWaiting && (
        <InboxModalHint>
          This matter has no open work items waiting on anything, so
          there's nothing to attach this document to.
        </InboxModalHint>
      )}

      {loadStatus === "ready" && items.length > 0 && (
        <>
          <InboxModalHint style={{ marginBottom: 12 }}>
            The document joins the work item. <strong>Attach</strong> leaves
            the item where it is (more to come); <strong>Attach &amp;
            return</strong> also sends it back to whoever asked — one step.
          </InboxModalHint>

          <InboxModalFieldGroup>
            <InboxModalLabel htmlFor="attach-work-item">
              Work item
            </InboxModalLabel>
            <Select
              id="attach-work-item"
              style={{ width: "100%" }}
              placeholder="Which work item does this answer?"
              options={options}
              value={workItemId}
              onChange={setWorkItemId}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </InboxModalFieldGroup>

          <InboxModalFieldGroup>
            <InboxModalLabel htmlFor="attach-note">
              Note (optional)
            </InboxModalLabel>
            <Input.TextArea
              id="attach-note"
              autoSize={{ minRows: 2, maxRows: 6 }}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Came in this morning's mail — looks complete."
              maxLength={4000}
            />
          </InboxModalFieldGroup>
        </>
      )}

      {error && (
        <InboxModalErrorBanner role="alert">{error}</InboxModalErrorBanner>
      )}
    </Modal>
  );
};

AttachToWorkItemModal.propTypes = {
  open: PropTypes.bool.isRequired,
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialWorkItemId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onAttached: PropTypes.func,
};

export default AttachToWorkItemModal;
